/**
 * The style knobs that CSS inherits, so setting one on a block is meant to
 * apply to all of the block's text rather than just its own text node.
 */
const INHERITED_STYLE_PROPERTIES = [
  "color",
  "font-size",
  "font-weight",
  "line-height",
  "text-align",
] as const;

/**
 * The leading given to a block that sizes its own text without saying how much
 * room each line needs. Unitless, so it stays proportional at any size.
 */
const PROPORTIONAL_LINE_HEIGHT = "1.4";

/**
 * Makes the inheritable style knobs authored on a block reach every descendant,
 * by stamping `inherit` on descendants that declare none of their own.
 *
 * Inheritance alone stops at the first descendant that declares the property,
 * and authored documents style their text with utility classes
 * (`text-gray-500`, `text-xs`, `prose`), so a knob set on a wrapper block would
 * otherwise do nothing at all. A descendant with its own inline value keeps it,
 * and its subtree then inherits from that instead. Repeater interiors live
 * inside `<template>`, which this does not descend into.
 *
 * Sizing a block's text also settles its leading, because a `text-*` size class
 * pins `line-height` to a fixed rem value that does not follow the size: left
 * alone, larger text overflows its line boxes and the paginator's
 * `overflow: hidden` clips it, and smaller text keeps the old gaps. `prose`
 * leading is unitless and already follows the size, so it is left as authored -
 * but only while `prose` is the one setting it. A block carrying both `prose`
 * and a size utility has its leading pinned in rems by that utility, which is
 * emitted after typography's rules and so wins at equal specificity, so it
 * gets the proportional leading like any other sized block.
 * @param block - The block whose subtree follows it (mutated in place)
 */
export function cascadeBlockStyles(block: HTMLElement): void {
  if (
    block.style.getPropertyValue("font-size") !== "" &&
    block.style.getPropertyValue("line-height") === "" &&
    !hasSelfSizingProseLeading(block)
  ) {
    block.style.setProperty("line-height", PROPORTIONAL_LINE_HEIGHT);
  }
  const authored = INHERITED_STYLE_PROPERTIES.filter(
    (property) => block.style.getPropertyValue(property) !== "",
  );
  if (authored.length === 0) {
    return;
  }
  cascadeProseColor(block);
  Array.from(block.querySelectorAll("*")).forEach((descendant) => {
    if (!(descendant instanceof HTMLElement)) {
      return;
    }
    authored
      .filter(
        (property) =>
          descendant.style.getPropertyValue(property) === "" &&
          !(
            property === "line-height" && hasSelfSizingProseLeading(descendant)
          ),
      )
      .forEach((property) => descendant.style.setProperty(property, "inherit"));
  });
}

/**
 * Tailwind's text-size utilities, which pin `line-height` alongside the size.
 * Color and alignment utilities share the `text-` prefix but set neither.
 */
const TEXT_SIZE_CLASS = /^text-(xs|sm|base|lg|xl|\d+xl)$/;

/**
 * Whether the element's leading comes from `prose` alone, and so already
 * follows its font size.
 * @param element - The element to inspect
 * @returns True when it carries `prose` and no text-size utility
 */
function hasSelfSizingProseLeading(element: HTMLElement): boolean {
  return (
    element.classList.contains("prose") &&
    !Array.from(element.classList).some((className) =>
      TEXT_SIZE_CLASS.test(className),
    )
  );
}

/**
 * Tailwind typography's text-color variables. Code, keyboard keys and rules sit
 * on their own backgrounds, so they keep the theme's colors.
 */
const PROSE_TEXT_COLOR_VARIABLES = [
  "--tw-prose-body",
  "--tw-prose-headings",
  "--tw-prose-lead",
  "--tw-prose-links",
  "--tw-prose-bold",
  "--tw-prose-counters",
  "--tw-prose-bullets",
  "--tw-prose-quotes",
  "--tw-prose-captions",
];

/**
 * Points Tailwind typography's color variables at the block's own color, so
 * markdown rendered under it follows too. Markdown bodies are built by React
 * from field text and so have no elements in the source for the subtree walk
 * above to reach, and `prose` colors headings, bold and links through
 * descendant rules that beat inheritance anyway. The variables inherit, so
 * setting them on the block covers markdown nested at any depth beneath it.
 * @param block - The block whose color prose follows (mutated in place)
 */
function cascadeProseColor(block: HTMLElement): void {
  const color = block.style.getPropertyValue("color");
  if (color === "") {
    return;
  }
  PROSE_TEXT_COLOR_VARIABLES.forEach((variable) =>
    block.style.setProperty(variable, color),
  );
}
